import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Eye, Star, MessageSquarePlus, MessageCircleReply } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useTenant } from "@/hooks/useTenant";
import { usePreserveSearchParams } from "@/hooks/usePreserveSearchParams";
import { DashTile, type DashTileProps } from "./DashTile";

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

interface ReviewStats {
  avgStars: number;
  total: number;
  newThisWeek: number;
  newLastWeek: number;
  answered: number;
  answerRate: number;
}

function useGBPReviewStats(tenantId: string | null) {
  return useQuery({
    queryKey: ["gbpReviewStats", tenantId],
    enabled: !!tenantId,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<ReviewStats> => {
      const { data, error } = await supabase
        .from("gbp_reviews")
        .select("star_rating, review_reply, create_time")
        .eq("tenant_id", tenantId as string);
      if (error) throw error;
      const rows = data ?? [];
      const now = Date.now();
      const rated = rows.filter((r) => r.star_rating != null);
      const avgStars = rated.length
        ? rated.reduce((s, r) => s + (r.star_rating as number), 0) / rated.length
        : 0;
      const ageMs = (t: string | null) => (t ? now - new Date(t).getTime() : Infinity);
      const newThisWeek = rows.filter((r) => ageMs(r.create_time) <= WEEK_MS).length;
      const newLastWeek = rows.filter((r) => {
        const a = ageMs(r.create_time);
        return a > WEEK_MS && a <= 2 * WEEK_MS;
      }).length;
      const answered = rows.filter((r) => r.review_reply != null).length;
      return {
        avgStars,
        total: rows.length,
        newThisWeek,
        newLastWeek,
        answered,
        answerRate: rows.length ? Math.round((answered / rows.length) * 100) : 0,
      };
    },
  });
}

interface ProfileViews {
  thisWeek: number;
  lastWeek: number;
  change: number;
}

function useGBPProfileViews(tenantId: string | null) {
  return useQuery({
    queryKey: ["gbpProfileViews", tenantId],
    enabled: !!tenantId,
    staleTime: 60 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    queryFn: async (): Promise<ProfileViews | null> => {
      const { data, error } = await supabase.functions.invoke("gbp-profile-views", {
        body: { tenantId },
      });
      if (error) return null;
      const d = data as { ok?: boolean; thisWeek?: number; lastWeek?: number; change?: number };
      if (!d?.ok) return null;
      return { thisWeek: d.thisWeek ?? 0, lastWeek: d.lastWeek ?? 0, change: d.change ?? 0 };
    },
  });
}

export const GBPReviewTiles: React.FC = () => {
  const { currentTenant } = useTenant();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { createPath } = usePreserveSearchParams();
  const tenantId = currentTenant?.id ?? null;
  const isConnected = !!currentTenant?.gbp_location_id;
  const goToReviews = () => navigate(createPath("/analytics/bewertungen"));

  const { data: stats } = useGBPReviewStats(isConnected ? tenantId : null);
  const { data: views, isLoading: viewsLoading } = useGBPProfileViews(
    isConnected ? tenantId : null,
  );

  // Only show this row for tenants whose Google Business Profile is connected.
  if (!isConnected) return null;

  const locale = i18n.language === "de" ? "de-DE" : "en-US";
  const nf = (n: number) => n.toLocaleString(locale);
  const signed = (n: number) => `${n >= 0 ? "+" : ""}${nf(n)} ${t("vsLastWeek")}`;

  const newDelta = (stats?.newThisWeek ?? 0) - (stats?.newLastWeek ?? 0);

  const tiles: DashTileProps[] = [
    {
      title: t("gbpProfileViews", { defaultValue: "Google Profilaufrufe" }),
      value: viewsLoading ? "…" : nf(views?.thisWeek ?? 0),
      sub: views ? signed(views.change) : t("vsLastWeek"),
      tooltip: t("gbpProfileViewsTooltip", {
        defaultValue:
          "Wie oft dein Google-Profil diese Woche in Google Suche und Maps angezeigt wurde. Googles Daten hinken etwa 2-3 Tage hinterher.",
      }),
      icon: Eye,
      color: "text-primary",
      bgColor: "bg-primary/10",
    },
    {
      title: t("gbpAvgRating", { defaultValue: "Ø Bewertung" }),
      value: stats ? `${stats.avgStars.toLocaleString(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 })} ★` : "…",
      sub: t("gbpFromReviews", { count: stats?.total ?? 0, defaultValue: `aus ${stats?.total ?? 0} Bewertungen` }),
      tooltip: t("gbpAvgRatingTooltip", {
        defaultValue: "Der Durchschnitt aller deiner Google-Sterne-Bewertungen.",
      }),
      icon: Star,
      color: "text-amber-600",
      bgColor: "bg-amber-50",
      onClick: goToReviews,
    },
    {
      title: t("gbpNewReviews", { defaultValue: "Neue Bewertungen" }),
      value: stats ? nf(stats.newThisWeek) : "…",
      sub: signed(newDelta),
      tooltip: t("gbpNewReviewsTooltip", {
        defaultValue:
          "Neue Google-Bewertungen der letzten 7 Tage, im Vergleich zur Woche davor.",
      }),
      icon: MessageSquarePlus,
      color: "text-green-600",
      bgColor: "bg-green-50",
      onClick: goToReviews,
    },
    {
      title: t("gbpAnswerRate", { defaultValue: "Antwortquote" }),
      value: stats ? `${stats.answerRate} %` : "…",
      sub: t("gbpAnsweredOf", {
        answered: stats?.answered ?? 0,
        total: stats?.total ?? 0,
        defaultValue: `${stats?.answered ?? 0} von ${stats?.total ?? 0} beantwortet`,
      }),
      tooltip: t("gbpAnswerRateTooltip", {
        defaultValue:
          "Anteil deiner Bewertungen, die bereits beantwortet sind. Sylva beantwortet deine Bewertungen automatisch.",
      }),
      icon: MessageCircleReply,
      color: "text-blue-600",
      bgColor: "bg-blue-50",
      onClick: goToReviews,
    },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
      {tiles.map((tile) => (
        <DashTile key={tile.title} {...tile} />
      ))}
    </div>
  );
};
